import type { Identity } from './tokenStorage';

function normalizeStringArray(x: unknown): string[] | undefined {
    if (!Array.isArray(x)) return undefined;
    const out = x.map(i => String(i)).filter(s => s.length > 0);
    return out.length > 0 ? out : [];
}

function normalizeAccessRights(x: unknown): Identity['accessRights'] {
    if (x == null || typeof x !== 'object' || Array.isArray(x)) return null;
    return x as Identity['accessRights'];
}

export function normalizeIdentityPayload(x: unknown): Identity | null {
    if (x == null || typeof x !== 'object') return null;
    const raw = x as Record<string, unknown>;
    const id = raw.id ?? raw.Id;
    if (id == null || String(id) === '') return null;

    const licLocked =
        raw.companyLicenseLocked === true ||
        raw.CompanyLicenseLocked === true ||
        false;

    const licDays =
        typeof raw.licenseDaysRemaining === 'number'
            ? raw.licenseDaysRemaining
            : typeof raw.LicenseDaysRemaining === 'number'
              ? raw.LicenseDaysRemaining
              : undefined;

    const licGrace =
        typeof raw.licenseGracePeriodDays === 'number'
            ? raw.licenseGracePeriodDays
            : typeof raw.LicenseGracePeriodDays === 'number'
              ? raw.LicenseGracePeriodDays
              : undefined;

    const licExp =
        raw.licenseExpiryDateUtc != null
            ? String(raw.licenseExpiryDateUtc)
            : raw.LicenseExpiryDateUtc != null
              ? String(raw.LicenseExpiryDateUtc)
              : undefined;

    const warn =
        raw.companyLicenseWarningMessage != null
            ? String(raw.companyLicenseWarningMessage)
            : raw.CompanyLicenseWarningMessage != null
              ? String(raw.CompanyLicenseWarningMessage)
              : undefined;

    const licInGrace =
        raw.licenseInGracePeriod === true || raw.LicenseInGracePeriod === true ? true : false;

    const licEnds =
        raw.licenseAccessEndsOnUtc != null
            ? String(raw.licenseAccessEndsOnUtc)
            : raw.LicenseAccessEndsOnUtc != null
              ? String(raw.LicenseAccessEndsOnUtc)
              : undefined;

    return {
        id: String(id),
        fullName: String(raw.fullName ?? raw.FullName ?? ''),
        role: raw.role != null ? String(raw.role) : raw.Role != null ? String(raw.Role) : undefined,
        companyId:
            typeof raw.companyId === 'number'
                ? raw.companyId
                : typeof raw.CompanyId === 'number'
                  ? raw.CompanyId
                  : undefined,
        companyName:
            raw.companyName != null
                ? String(raw.companyName)
                : raw.CompanyName != null
                  ? String(raw.CompanyName)
                  : undefined,
        companyImage:
            raw.companyImage != null
                ? String(raw.companyImage)
                : raw.CompanyImage != null
                  ? String(raw.CompanyImage)
                  : null,
        profileImage:
            raw.profileImage != null
                ? String(raw.profileImage)
                : raw.ProfileImage != null
                  ? String(raw.ProfileImage)
                  : undefined,
        companyIsActivated:
            raw.companyIsActivated === false || raw.CompanyIsActivated === false ? false : true,
        companyLicenseLocked: licLocked,
        licenseExpiryDateUtc: licExp,
        licenseDaysRemaining: licDays,
        licenseGracePeriodDays: licGrace,
        licenseInGracePeriod: licInGrace,
        licenseAccessEndsOnUtc: licEnds,
        companyLicenseWarningMessage: warn,
        accessRights: normalizeAccessRights(raw.accessRights ?? raw.AccessRights),
        apps: normalizeStringArray(raw.apps ?? raw.Apps),
        permissions: normalizeStringArray(raw.permissions ?? raw.Permissions),
        lastUsedAppId:
            raw.lastUsedAppId != null
                ? String(raw.lastUsedAppId)
                : raw.LastUsedAppId != null
                  ? String(raw.LastUsedAppId)
                  : null,
        defaultAppId:
            raw.defaultAppId != null
                ? String(raw.defaultAppId)
                : raw.DefaultAppId != null
                  ? String(raw.DefaultAppId)
                  : null,
    };
}
